import { withStyleNonce } from '@/lib/csp-nonce';
import {
  CheckCircle,
  FileDashed,
  Info,
  MagnifyingGlass,
  MagicWand,
  X,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui-kit/sheet';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { getTemplateById } from '../../services/templates.service';
import { useGetTemplates } from '../../hooks/use-templates';
import {
  TEMPLATE_CATEGORIES,
  type Template,
  type TemplateCategory,
  type TemplateListItem,
} from '../../types/templates.types';
import { getTemplateErrorMessage } from '../../utils/template-api-errors';

const ALL_CATEGORIES = 'all';

const PREVIEW_SRCDOC_STYLE =
  'body{margin:0;font-family:Inter,system-ui,sans-serif;font-size:13px;color:#27272C;padding:12px}' +
  'img{max-width:100%}h1{font-size:18px;margin:0 0 8px}p{margin:0 0 6px;line-height:1.5}';

const toPreviewSrcDoc = (html: string): string => {
  const body = html.trim();
  if (!body) return '';
  if (body.includes('<html')) return body;
  return `<!doctype html><html><head><meta charset="utf-8"><style>${PREVIEW_SRCDOC_STYLE}</style></head><body>${body}</body></html>`;
};

export type TemplatePickerDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedId?: string | null;
  note?: string;
  confirmLabel?: string;
  title?: string;
  onChoose: (template: Template) => void | { ok: false } | Promise<void | { ok: false }>;
};

const formatUpdated = (iso: string): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const TemplateCardPreview = ({ template }: { template: TemplateListItem }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = hostRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '120px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const shouldLoadHtml = isVisible && !template.thumbnailUrl && template.hasContent;
  const {
    data: detail,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['template', template.itemId],
    queryFn: () => getTemplateById(template.itemId),
    staleTime: 30 * 1000,
    enabled: shouldLoadHtml,
    retry: false,
  });

  const previewHtml = detail?.html?.trim() || '';
  const srcDoc = previewHtml ? toPreviewSrcDoc(previewHtml) : '';

  if (template.thumbnailUrl) {
    return (
      <div
        ref={hostRef}
        className="relative h-[132px] flex-none overflow-hidden border-b border-neutral-200 bg-white"
      >
        <img
          src={template.thumbnailUrl}
          alt=""
          className="h-full w-full object-cover object-top"
          loading="lazy"
        />
      </div>
    );
  }

  if (!template.hasContent) {
    return (
      <div
        ref={hostRef}
        className="flex h-[132px] flex-none items-center justify-center border-b border-neutral-200 bg-neutral-50"
      >
        <span className="text-xs text-neutral-400">No content yet</span>
      </div>
    );
  }

  return (
    <div
      ref={hostRef}
      className="relative h-[132px] flex-none overflow-hidden border-b border-neutral-200 bg-white"
    >
      {srcDoc ? (
        <iframe
          title=""
          className="preview-frame pointer-events-none absolute left-0 top-0 origin-top-left border-0"
          style={{ width: 600, height: 420, transform: 'scale(0.44)' }}
          sandbox=""
          aria-hidden
          tabIndex={-1}
          srcDoc={withStyleNonce(srcDoc)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-neutral-50">
          {isLoading || !isVisible ? (
            <span className="text-xs text-neutral-400">Loading preview…</span>
          ) : isError ? (
            <FileDashed className="h-7 w-7 text-neutral-400" aria-hidden />
          ) : (
            <span className="text-xs text-neutral-400">No content yet</span>
          )}
        </div>
      )}
    </div>
  );
};

export const TemplatePickerDrawer = ({
  open,
  onOpenChange,
  selectedId = null,
  note = 'Copied into this campaign — the template itself is not linked.',
  confirmLabel = 'Use template',
  title = 'Choose a template',
  onChoose,
}: TemplatePickerDrawerProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState<string>(ALL_CATEGORIES);
  const [pickedId, setPickedId] = useState<string | null>(selectedId);
  const [initialId, setInitialId] = useState<string | null>(selectedId);
  const [showDiscard, setShowDiscard] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!open) return;
    setPickedId(selectedId ?? null);
    setInitialId(selectedId ?? null);
    setSearch('');
    setDebouncedSearch('');
    setCategory(ALL_CATEGORIES);
    setShowDiscard(false);
    setIsApplying(false);
  }, [open, selectedId]);

  const { data, isLoading, isError, error } = useGetTemplates(
    {
      pageNo: 1,
      pageSize: 100,
      sort: 'updated',
      order: 'desc',
      q: debouncedSearch || undefined,
      category: category === ALL_CATEGORIES ? undefined : (category as TemplateCategory),
    },
    { enabled: open }
  );

  const templates = data?.items ?? [];
  const totalUnfiltered = data?.totalUnfiltered ?? data?.totalCount ?? 0;
  const isDirty = pickedId !== initialId;

  const emptyMessage = useMemo(() => {
    if (isLoading) return null;
    if (totalUnfiltered === 0 && !debouncedSearch && category === ALL_CATEGORIES) {
      return 'empty';
    }
    if (templates.length === 0) return 'no-match';
    return null;
  }, [isLoading, totalUnfiltered, debouncedSearch, category, templates.length]);

  const requestClose = () => {
    if (isApplying) return;
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    onOpenChange(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (next) {
      onOpenChange(true);
      return;
    }
    requestClose();
  };

  const handleUse = async () => {
    if (!pickedId || isApplying) return;
    setIsApplying(true);
    try {
      const full = await queryClient.fetchQuery({
        queryKey: ['template', pickedId],
        queryFn: () => getTemplateById(pickedId),
        staleTime: 30 * 1000,
      });
      const result = await onChoose(full);
      if (result && result.ok === false) {
        setIsApplying(false);
        return;
      }
      onOpenChange(false);
    } catch (chooseError) {
      toast({
        variant: 'destructive',
        title: 'Failed to load template',
        description: getTemplateErrorMessage(chooseError, 'Please try again.'),
      });
      setIsApplying(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        overlayClassName="bg-black/40"
        className="flex w-full max-w-[880px] flex-col gap-0 overflow-hidden border-neutral-200 bg-white p-0 shadow-e4 sm:max-w-[880px] [&>button]:hidden"
      >
        <div className="flex h-14 flex-none items-center justify-between gap-3 border-b border-neutral-200 px-5">
          <div className="min-w-0">
            <SheetTitle className="inline text-base font-semibold text-neutral-900">
              {title}
            </SheetTitle>
            {note ? (
              <span className="ml-2 hidden text-xs text-neutral-500 sm:inline">{note}</span>
            ) : null}
            <SheetDescription className="sr-only">
              Pick a template to copy into this campaign
            </SheetDescription>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-neutral-500 hover:text-neutral-800"
            aria-label="Close"
            onClick={requestClose}
          >
            <X className="h-[18px] w-[18px]" />
          </Button>
        </div>

        {showDiscard ? (
          <div
            className="flex flex-none items-center justify-between gap-3 border-b border-neutral-200 px-5 py-3"
            style={{ background: '#FBEFDD' }}
          >
            <span className="text-xs text-neutral-800">
              Close without using the template you picked?
            </span>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowDiscard(false)}>
                Keep choosing
              </Button>
              <Button
                type="button"
                size="sm"
                className="bg-destructive text-white hover:bg-destructive/90"
                onClick={() => onOpenChange(false)}
              >
                Discard
              </Button>
            </div>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto bg-neutral-50 p-5">
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <div className="min-w-[200px] flex-1 space-y-1.5">
              <Label htmlFor="tpl-pick-search">Search templates</Label>
              <div className="relative">
                <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                <Input
                  id="tpl-pick-search"
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by name…"
                  className="h-10 pl-9"
                  autoFocus
                />
              </div>
            </div>
            <div className="w-[200px] space-y-1.5">
              <Label htmlFor="tpl-pick-cat">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="tpl-pick-cat" className="h-10">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_CATEGORIES}>All categories</SelectItem>
                  {TEMPLATE_CATEGORIES.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isLoading ? (
            <div className="rounded-lg border border-dashed border-neutral-300 bg-white px-4 py-10 text-center text-sm text-neutral-500">
              Loading templates…
            </div>
          ) : null}

          {isError ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-8 text-center text-sm text-destructive">
              {getTemplateErrorMessage(error, 'Failed to load templates')}
            </div>
          ) : null}

          {emptyMessage === 'empty' ? (
            <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-4 py-10 text-center">
              <FileDashed className="mx-auto h-7 w-7 text-neutral-400" aria-hidden />
              <p className="mt-2 text-sm text-neutral-700">No templates yet</p>
              <p className="mb-3 mt-1 text-xs text-neutral-500">
                Start from a blank canvas, or build a reusable layout first.
              </p>
              <Button type="button" variant="outline" size="sm" asChild>
                <Link to="/templates">Create a template</Link>
              </Button>
            </div>
          ) : null}

          {emptyMessage === 'no-match' ? (
            <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-4 py-10 text-center">
              <MagnifyingGlass className="mx-auto h-7 w-7 text-neutral-400" aria-hidden />
              <p className="mt-2 text-sm text-neutral-700">No template matches that search</p>
              <p className="mt-1 text-xs text-neutral-500">
                Try a different name, or clear the category filter.
              </p>
            </div>
          ) : null}

          {!isLoading && !isError && templates.length > 0 ? (
            <div
              role="radiogroup"
              aria-label="Templates"
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            >
              {templates.map((template) => {
                const selected = pickedId === template.itemId;
                const name = template.name.trim() || 'Untitled template';
                return (
                  <button
                    key={template.itemId}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() =>
                      setPickedId((current) =>
                        current === template.itemId ? null : template.itemId
                      )
                    }
                    className={cn(
                      'flex flex-col overflow-hidden rounded-lg border-2 bg-white text-left transition-colors',
                      selected
                        ? 'border-primary-600 shadow-e2'
                        : 'border-neutral-200 hover:border-neutral-400'
                    )}
                  >
                    <TemplateCardPreview template={template} />
                    <span className="flex flex-1 flex-col gap-1 p-3">
                      <span className="flex items-start justify-between gap-2">
                        <span className="text-sm font-medium leading-snug text-neutral-900">
                          {name}
                        </span>
                        {selected ? (
                          <CheckCircle
                            className="h-[18px] w-[18px] flex-none text-primary-600"
                            weight="fill"
                            aria-hidden
                          />
                        ) : null}
                      </span>
                      <span className="text-xs text-neutral-500">
                        {template.category || 'uncategorised'} · updated{' '}
                        {formatUpdated(template.updatedAt)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}

          <p className="mt-4 flex items-start gap-2 text-xs text-neutral-500">
            <Info className="mt-px h-[15px] w-[15px] flex-none" aria-hidden />
            <span>
              Choosing a template <strong>copies</strong> its content into this campaign. Editing
              the template later will not change the campaign, and the campaign’s current content is
              kept as an earlier version.
            </span>
          </p>
        </div>

        <div className="flex flex-none items-center justify-end gap-3 border-t border-neutral-200 px-5 py-3">
          <Button type="button" variant="ghost" onClick={requestClose} disabled={isApplying}>
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-primary-600 text-white hover:bg-primary-700"
            disabled={!pickedId || isApplying}
            onClick={() => {
              void handleUse();
            }}
          >
            <MagicWand className="mr-1.5 h-4 w-4" />
            {isApplying ? 'Applying…' : confirmLabel}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
